import { Destination } from "../common/ObjectTypes";
import { getDestinationDetails } from "../../api/destinations/destinationsApi";
import { isEarthRangerDestinationType } from "./earthRangerUrl";

type FilterTags = {
    name: string,
    id: string
};

// For EarthRanger destinations, returns the account identifier from the auth
// config: the username when set, otherwise the first 5 characters of the token.
// Returns null for non-ER destinations or when no auth config is available.
export const getDestinationAccount = (destination: Destination): string | null => {
    if (!isEarthRangerDestinationType(destination.type?.name)) return null;
    const configs = destination.configurations;
    if (!Array.isArray(configs)) return null;
    // action.type is the stable discriminator for auth actions; action.value
    // is action-specific (e.g. "auth" for ER but can differ per integration type)
    const authData = configs.find((c: any) => c.action?.type === "auth")?.data;
    if (!authData) return null;
    if (authData.username) return String(authData.username);
    if (authData.token) return `${String(authData.token).slice(0, 5)}…`;
    return null;
}

// Reduce a fetched configurations array to just what the Account column
// needs: the auth config with the username and a truncated token. Keeps
// full auth tokens out of long-lived React state.
const sanitizeAuthConfigurations = (configurations: any): any[] => {
    const authConfig = Array.isArray(configurations)
        ? configurations.find((c: any) => c.action?.type === "auth")
        : undefined;
    if (!authConfig) return [];
    return [{
        action: authConfig.action,
        data: {
            username: authConfig.data?.username,
            token: authConfig.data?.token ? String(authConfig.data.token).slice(0, 5) : undefined,
        },
    }];
}

// The Route details endpoint returns destinations without their
// configurations, so the auth account can't be derived from them directly.
// Fetch full details for EarthRanger destinations that are missing them.
// Failed fetches fall back to an empty configurations array so callers can
// tell the destination was processed and won't refetch it.
export const enrichDestinationsWithAuth = async (token: string, destinations: Destination[]): Promise<Destination[]> => {
    return Promise.all(destinations.map(async (dest) => {
        if (!isEarthRangerDestinationType(dest.type?.name) || dest.configurations) return dest;
        try {
            const details = await getDestinationDetails(token, dest.id);
            return { ...dest, configurations: sanitizeAuthConfigurations(details?.configurations) };
        } catch (err) {
            return { ...dest, configurations: [] };
        }
    }));
}

// True when at least one EarthRanger destination still needs its auth config fetched
export const destinationsNeedAuthEnrichment = (destinations: Destination[]): boolean =>
    destinations.some(dest => isEarthRangerDestinationType(dest.type?.name) && !dest.configurations);

export const changeDestinationGlobalSearch = (searchSubString: string, destinationFilter: any) => {
    destinationFilter.search = searchSubString;
    return destinationFilter;
}

export const filterDestinationOwner = (e: any, destinationFilter: any) => {
    let tempArray: FilterTags[] = destinationFilter.owner__in
    if (tempArray.map(a => a.id).includes(e.target.value)) {
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== e.target.value) removed.push(tempArray[item])
        }
        destinationFilter[e.target.id] = removed
    } else {
        let newTag = {
            name: e.target.name,
            id: e.target.value
        }
        tempArray.push(newTag)
        destinationFilter[e.target.id] = tempArray
    }
    return destinationFilter
}

export const filterDestinationUrl = (e: any, destinationFilter: any) => {
    let tempArray: FilterTags[] = destinationFilter.base_url__in
    if (tempArray.map(a => a.id).includes(e.target.value)) {
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== e.target.value) removed.push(tempArray[item])
        }
        destinationFilter[e.target.id] = removed
    } else {
        let newTag = {
            name: e.target.name,
            id: e.target.value
        }
        tempArray.push(newTag)
        destinationFilter[e.target.id] = tempArray
    }
    return destinationFilter;
}

export const filterDestinationType = (e: any, destinationFilter: any) => {
    let tempArray: FilterTags[] = destinationFilter.type__in
    if (tempArray.map(a => a.id).includes(e.target.value)) {
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== e.target.value) {
                removed.push(tempArray[item])
            }

        }
        destinationFilter[e.target.id] = removed
    } else {
        let newTag = {
            name: e.target.name,
            id: e.target.value
        }
        tempArray.push(newTag)
        destinationFilter[e.target.id] = tempArray
    }
    return destinationFilter;
}

export const removeDestinationFilterTag = (e: any, destinationFilter: any) => {
    if (destinationFilter.type__in.map((a: any) => a.id).includes(e)) {
        let tempArray: FilterTags[] = destinationFilter.type__in
        let removed: FilterTags[] = []
        for (var item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        destinationFilter.type__in = removed
    } else if (destinationFilter.owner__in.map((a: any) => a.id).includes(e)) {
        let tempArray: FilterTags[] = destinationFilter.owner__in
        let removed: FilterTags[] = []
        for (item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        destinationFilter.owner__in = removed
    } else if ((destinationFilter.base_url__in.map((a: any) => a.id).includes(e))) {
        let tempArray: FilterTags[] = destinationFilter.base_url__in
        let removed: FilterTags[] = []
        for (item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        destinationFilter.base_url__in = removed
    } else {
        // enabled
        let tempArray: FilterTags[] = destinationFilter.enabled
        let removed: FilterTags[] = []
        for (item in tempArray) {
            if (tempArray[item].id !== e) removed.push(tempArray[item])
        }
        destinationFilter.enabled = removed
    }
    return destinationFilter;
}

export const resetDestinationFilters = () => {
    return {
        base_url__in: [],
        enabled: [],
        type__in: [],
        owner__in: [],
        ordering: "",
        search: ""
    }
}